// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/** A hole's scorecard row: what the course was configured with, not what the map shows. */
export type CourseHole = {
  number: number;
  par: number | null;
  /** The hole's handicap ranking, 1–18. Null on a course nobody has configured one for. */
  strokeIndex: number | null;
  /** As entered on the course-config screen. Null when nobody typed one in. */
  lengthMeters: number | null;
  /**
   * The same hole measured from each individual tee. Empty on a course where nobody has
   * entered per-tee lengths, and on a server that predates them.
   */
  teeLengths: CourseHoleTeeLength[];
  /**
   * The hole with no tee in mind, once per source that measured it. Separate from
   * {@link lengthMeters}, which is the single number the server picked for a reader — these are
   * the numbers it picked between.
   */
  lengths: CourseHoleLength[];
  /**
   * What each source states this hole's par and stroke index to be, one entry per source.
   *
   * Separate from {@link par} and {@link strokeIndex}, which are the single values the server
   * propagated and every playing path reads. A club's published card lands here whether or not
   * CLAUDE.md §18 let it reach the hole itself — so a hole showing a placeholder par still
   * carries the real one, and this is the only place it is visible.
   */
  cards: CourseHoleCard[];
};

/** One source's stated par and stroke index for a hole. */
export type CourseHoleCard = {
  /** "manual" | "golfisverige" | "osm" | "unknown" — anything at all on an old row. */
  source: string;
  par: number;
  strokeIndex: number;
};

/** One hole's general length, as one source measured it. */
export type CourseHoleLength = {
  /** "analysis" | "manual" | "golfisverige" | "osm" | "unknown" — anything at all on an old row. */
  source: string;
  lengthMeters: number;
};

/**
 * One hole's length from one named tee, as one source measured it.
 *
 * One entry per (tee, source): a tee carries the club's card length and our own measurement at
 * once, and the source is the only thing telling two rows for the same tee apart.
 */
export type CourseHoleTeeLength = {
  teeId: string;
  teeName: string;
  /** "male" | "female" | "unisex" — two tees can share a name and differ only in this. */
  sex: string;
  /** "analysis" | "manual" | "golfisverige" | "osm" | "unknown". */
  source: string;
  lengthMeters: number;
};

type HoleResponse = Record<string, unknown>;

/** What a length with no stated source is called, both on the wire and here. */
export const UNKNOWN_SOURCE = 'unknown';

const num = (row: HoleResponse, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const str = (row: HoleResponse, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const mapTeeLength = (row: HoleResponse): CourseHoleTeeLength | null => {
  const teeId = str(row, 'teeId', 'TeeId');
  const lengthMeters = num(row, 'lengthMeters', 'LengthMeters');
  if (!teeId || lengthMeters === null || lengthMeters <= 0) return null;
  return {
    teeId,
    teeName: str(row, 'teeName', 'TeeName'),
    sex: str(row, 'sex', 'Sex'),
    // A row from a server that predates per-source storage says nothing about who measured it,
    // which is exactly what "unknown" means — not a gap to be filled in with a guess.
    source: str(row, 'source', 'Source') || UNKNOWN_SOURCE,
    lengthMeters,
  };
};

const mapCard = (row: HoleResponse): CourseHoleCard | null => {
  const par = num(row, 'par', 'Par');
  const strokeIndex = num(row, 'strokeIndex', 'StrokeIndex');
  // Both or neither: a card stating one of the two is not a card, and half a row would render
  // as a disagreement with the hole that nobody actually claimed.
  if (par === null || par <= 0 || strokeIndex === null || strokeIndex <= 0) return null;
  return { source: str(row, 'source', 'Source') || UNKNOWN_SOURCE, par, strokeIndex };
};

const mapLength = (row: HoleResponse): CourseHoleLength | null => {
  const lengthMeters = num(row, 'lengthMeters', 'LengthMeters');
  if (lengthMeters === null || lengthMeters <= 0) return null;
  return { source: str(row, 'source', 'Source') || UNKNOWN_SOURCE, lengthMeters };
};

/**
 * One hole of a course-detail response. Exported for the other readers of that same endpoint —
 * the shape is the server's, and a second copy of this mapping would be a second thing to keep
 * in step with it.
 */
export const mapCourseHole = (row: HoleResponse): CourseHole | null => {
  const number = num(row, 'number', 'Number');
  if (number === null || number <= 0) return null;
  const teeLengths = row.teeLengths ?? row.TeeLengths;
  const lengths = row.lengths ?? row.Lengths;
  const cards = row.cards ?? row.Cards;
  return {
    number,
    par: num(row, 'par', 'Par'),
    strokeIndex: num(row, 'strokeIndex', 'StrokeIndex'),
    lengthMeters: num(row, 'lengthMeters', 'LengthMeters'),
    teeLengths: Array.isArray(teeLengths)
      ? (teeLengths as HoleResponse[]).map(mapTeeLength).filter((l): l is CourseHoleTeeLength => l !== null)
      : [],
    lengths: Array.isArray(lengths)
      ? (lengths as HoleResponse[]).map(mapLength).filter((l): l is CourseHoleLength => l !== null)
      : [],
    // Absent on a server that predates the per-source card, which is the same answer as a course
    // no source has stated one for: nothing to compare the hole against.
    cards: Array.isArray(cards)
      ? (cards as HoleResponse[]).map(mapCard).filter((c): c is CourseHoleCard => c !== null)
      : [],
  };
};

/**
 * The course's scorecard holes — par and the entered length, by hole number.
 *
 * Separate from {@link useHoleFeatures}, which reads the geometry: par is not a mapped thing
 * and never arrives with the tees and greens. Empty on any failure, so a caller renders
 * without it rather than showing a wrong par.
 */
export function useCourseHoles(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): CourseHole[] {
  const [holes, setHoles] = useState<CourseHole[]>([]);

  useEffect(() => {
    if (!token || !courseId) {
      setHoles([]);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!response.ok) {
          setHoles([]);
          return;
        }
        const payload = (await response.json()) as { holes?: HoleResponse[]; Holes?: HoleResponse[] };
        if (cancelled) return;
        const rows = payload.holes ?? payload.Holes ?? [];
        setHoles(Array.isArray(rows) ? rows.map(mapCourseHole).filter((h): h is CourseHole => h !== null) : []);
      } catch {
        if (!cancelled) setHoles([]);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token]);

  return holes;
}
